import React, { useEffect, useState } from "react";
import moment from "moment";
import Spinner from "common/Spinner";
import SearchStripe from "./ui/SearchStripe";
import TableHeader from "./ui/TableHeader";
import TableContent from "./ui/TableContent";
import Pagination from "./ui/Pagination";
import styles from "./ObitsListing.module.css";
import SortOptionsForMobile from "./ui/SortOptionsForMobile";
import { obitsListingService } from "services";
import { getFormattedItemsPath } from "utils/getFormattedItemsPath";
import { IObitsSearchParams } from "services/obits-listing/obits-listing-service";

const obitsPerPage = 10;
const tomorrow = moment().add(1, "d").format("YYYY-MM-DD");

const ObitsListingWrapper = (props) => {
  const { itemsPath = "", isBranch } = props;
  const searchPath = getFormattedItemsPath(itemsPath, isBranch);
  const [obits, setObits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState([]);
  const [numberOfPages, setNumberOfPages] = useState<number>(0);
  const [deceasedName, setDeceasedName] = useState<string>("");
  const [isOrderedByDesc, setIsOrderedByDesc] = useState<boolean>(true);
  const [selectedSortBy, setSelectedSortBy] = useState<
    "deceasedName" | "deceasedBirthDate" | "deceasedDeathDate"
  >("deceasedDeathDate");
  const initialDateRange = isBranch
    ? {
        id: "default_branch",
        value: "Past year",
        startDate: moment().subtract(1, "y").format("YYYY-MM-DD"),
        endDate: tomorrow,
        disabled: false,
      }
    : {
        id: "default_corp",
        value: "Past 30 days",
        startDate: moment().subtract(30, "d").format("YYYY-MM-DD"),
        endDate: tomorrow,
        disabled: false,
      };
  const [selectedDateRange, setSelectedDateRange] = useState(initialDateRange);

  const searchObits = async (pageNumber: number) => {
    const searchParams: IObitsSearchParams = {
      name: deceasedName,
      minDate: selectedDateRange.startDate,
      maxDate: selectedDateRange.endDate,
      orderBy: selectedSortBy,
      orderBySort: isOrderedByDesc ? "desc" : "asc",
      limit: obitsPerPage,
      pageNumber,
      searchPath,
    };
    try {
      setLoading(true);
      const resData = await obitsListingService.searchObits(
        itemsPath,
        searchParams
      );
      setObits(resData.results);
      setPagination(resData.pagination);
      setNumberOfPages(Math.ceil(resData.resultTotal / obitsPerPage) || 1);
    } catch (error) {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchObits(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSortBy, isOrderedByDesc]);

  return (
    <div className={styles.obitsListWrapper}>
      <SearchStripe
        name={deceasedName}
        setName={setDeceasedName}
        dateRange={selectedDateRange}
        setDateRange={setSelectedDateRange}
        isBranch={isBranch}
        path={searchPath}
        searchObits={searchObits}
      />
      <SortOptionsForMobile
        sortBy={selectedSortBy}
        setSortBy={setSelectedSortBy}
      />
      <div className={styles.resultsTableWrapper}>
        <TableHeader
          sortBy={selectedSortBy}
          setSortBy={setSelectedSortBy}
          isOrderedByDesc={isOrderedByDesc}
          setIsOrderedByDesc={setIsOrderedByDesc}
        />
        {loading ? <Spinner /> : <TableContent obits={obits} />}
      </div>
      {!loading && (
        <Pagination
          paginationData={pagination}
          numberOfPages={numberOfPages}
          searchObits={searchObits}
        />
      )}
    </div>
  );
};

const ObitsListing = (props) => {
  // render nothing if component not configured
  if (!props) {
    return null;
  }
  // render ObitsListing component if component configured
  // pass props to ObitsListingWrapper. These props include the mapped properties from AEM JSON
  return <div>{ObitsListingWrapper(props)}</div>;
};

export default ObitsListing;
